import { useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { LayoutDashboard, ShieldCheck, MessagesSquare, MessageCircle, Users, FolderTree, Search } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const NAV = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, key: "dashboard" },
  { to: "/admin/moderation", label: "Moderation", icon: ShieldCheck, key: "moderation" },
  { to: "/admin/discussions", label: "Discussions", icon: MessagesSquare, key: "discussions" },
  { to: "/admin/replies", label: "Replies", icon: MessageCircle, key: "replies" },
  { to: "/admin/members", label: "Members", icon: Users, key: "members" },
  { to: "/admin/categories", label: "Categories", icon: FolderTree, key: "categories" },
  { to: "/admin/seo", label: "SEO", icon: Search, key: "seo" },
];

export default function AdminShell({ active, title, subtitle, children }) {
  const { user, ready } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (ready && (user === false || (user && user.role !== "admin"))) navigate("/");
  }, [ready, user, navigate]);

  if (!ready || !user || user.role !== "admin") {
    return <div className="mx-auto max-w-4xl px-6 py-24 text-center text-mutedplum">Checking access…</div>;
  }

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 py-8 grid lg:grid-cols-[240px_1fr] gap-8">
      <aside className="lg:sticky lg:top-24 h-max">
        <div className="rounded-2xl bg-plum text-cream p-4">
          <p className="px-2 pb-3 text-xs uppercase tracking-widest text-rose/80 font-semibold">Admin portal</p>
          <nav className="space-y-1" data-testid="admin-nav">
            {NAV.map((n) => {
              const on = active === n.key || location.pathname === n.to;
              const Icon = n.icon;
              return (
                <Link key={n.key} to={n.to} data-testid={`admin-nav-${n.key}`}
                  className={`flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors ${on ? "bg-cream text-plum" : "text-cream/85 hover:bg-white/10"}`}>
                  <Icon className="h-4 w-4" /> {n.label}
                </Link>
              );
            })}
          </nav>
        </div>
      </aside>

      <section className="min-w-0">
        <header className="mb-6">
          <h1 className="font-display text-3xl sm:text-4xl text-plum" data-testid="admin-page-title">{title}</h1>
          {subtitle && <p className="text-mutedplum mt-1">{subtitle}</p>}
        </header>
        {children}
      </section>
    </div>
  );
}
